// User element in header including menu

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from '../utils/firebase';
import { useFirebase } from './FirebaseContextProvider';
import { useModal } from './ModalProvider';
import ContextMenu from './ContextMenu';
import NotificationsModalBody from './NotificationsModalBody';
import OrganizationModalBody from './OrganizationModalBody';
import OrganizationSwitchModalBody from './OrganizationSwitchModalBody';
import { disableNotifications } from '../utils/notifications';
import { UsersRoles } from '../utils/enums';
import './User.scss';

export default function User() {
  const [showUserMenu, setShowUserMenu] = useState(false);
  const { user, activeOrg, myOrgs, orgsById } = useFirebase();
  const navigate = useNavigate();
  const { openModal } = useModal();

  const handleLogout = async () => {
    // Best-effort token cleanup (needs auth, so before signOut), but never let a
    // slow getToken/SW block logout — cap the wait.
    try {
      await Promise.race([
        disableNotifications(),
        new Promise((resolve) => setTimeout(resolve, 3000)),
      ]);
    } catch (error) {
      // ignore — logout proceeds regardless
    }
    signOut(auth);
    openModal({
      header: 'Logged out',
      body: (
        <p className='font-16'>
          You have successfully logged out. Thanks for using Muva!
        </p>
      ),
      buttons: [{ text: 'OK', onClick: () => navigate('/') }],
      onClose: () => navigate('/'),
    });
  };

  const handleNotifications = () => {
    openModal({
      header: 'Notifications',
      body: <NotificationsModalBody />,
    });
  };

  const handleOrganization = () => {
    openModal({
      header: 'Organization',
      body: <OrganizationModalBody />,
    });
  };

  const handleSwitchOrganization = () => {
    openModal({
      header: 'Switch organization',
      body: <OrganizationSwitchModalBody />,
    });
  };

  const userMenuOptions = [
    {
      item: user?.role === UsersRoles.ADMIN ? 'Admin panel' : 'Profile',
      onClick: () => navigate('/login'),
    },
    // The one place the active organization is surfaced: the admin panel has no
    // profile tab, so a row in the user cards would never reach an admin.
    ...(activeOrg
      ? [
          {
            // Falls back to the id while the organization document is still read
            item: `Organization: ${orgsById.get(activeOrg)?.name ?? activeOrg}`,
            // The organization window: its profile and admins
            onClick: handleOrganization,
          },
        ]
      : []),
    ...(myOrgs.length > 1
      ? [{ item: 'Switch organization', onClick: handleSwitchOrganization }]
      : []),
    { item: 'Notifications', onClick: handleNotifications },
    { item: 'Log out', onClick: handleLogout },
  ];

  if (!user)
    return (
      <div className='user'>
        <button className='small bw' onClick={() => navigate('/login')}>
          Log in
        </button>
      </div>
    );

  return (
    <div className='user' onClick={() => setShowUserMenu(true)}>
      <p className='icon fa-user' />
      <p className='name upper font-medium'>{user.name}</p>
      <ContextMenu
        show={showUserMenu}
        onClose={() => setShowUserMenu(false)}
        options={userMenuOptions}
        className='user-menu'
      />
    </div>
  );
}
